import React, {useEffect, useState} from 'react';
import axios from "axios"
import {
  TextField,
  Button,
  Grid,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Divider,
  Typography,
  CircularProgress,
  Box,
  MenuItem
} from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from "react-i18next";
import FeedbackIcon from '../assets/FeedbackIcon.png';
import { useAuthStore  } from '../stores/zustand';

/**
 * Refactored Feedback Component
 * 
 * A clean, focused component that:
 * - Uses custom hook for business logic
 * - Uses service layer for data operations
 * - Focuses only on UI presentation
 * - Provides better error handling and validation
 * 
 * @component
 */
const FeedbackComponent = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const {user} = useAuthStore ()
  const [isLoged, setIsLoged] = useState(true);
  // Form state
  const [loading, setLoading] = useState(false);
  const [isSent, setIsSent] = useState(false)
  const [sendFailed, setSendFailed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    area: "physio"
  });
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = t('firstNameRequired') || 'Name is required';
    }
    if (!formData.email.trim()) {
      errors.email = t('passwordRequired').replace('Password', 'Email') || 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('invalidEmail') || 'Invalid email address';
    }
    if (!formData.message.trim()) {
      errors.message = t('messageRequired') || 'Message is required';
    }
    return errors;
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_FIREBASE_API_HOST}/api/contact`, formData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data) {
        setIsSent(true);
      } else {
        setSendFailed(true);
      }

      setFormData({ area: "physio", name: "", email: "", message: "" });
      setFieldErrors({});
    } catch (error) {
      console.error("Form error:", error.response?.data || error.message);
      setSendFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=>{
    if (user.name !== "") {
        setIsLoged(false)
      }
  },[user])  

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <div
        className={isMobile ? "white-background-mobile" : "white-background"}
        style={{ width: '100%' }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            padding: isMobile ? 0 : 3,
            justifyContent: 'center',
          }}
        >
          {/* Left column - only on non-mobile devices */}
          {!isMobile && (
            <>
              <Grid item xs={12} md={5}>
                <img 
                  src={FeedbackIcon} 
                  style={{ 
                    width: '124px', 
                    height: '124px', 
                    marginLeft: '-18px' 
                  }} 
                  alt="Feedback" 
                />
                <Typography 
                  variant="h4" 
                  style={{ 
                    marginTop: '42px', 
                    color: '#063bd0' 
                  }} 
                  gutterBottom
                >
                  <b>{t('importantOpinion')}</b>
                </Typography>
                <Typography 
                  variant="h6" 
                  style={{ 
                    marginTop: '18px', 
                    color: '#063bd0' 
                  }}
                >
                  {t('feedbackSubheader')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={1}>
                <Divider 
                  orientation="vertical" 
                  sx={{ 
                    width: '2px', 
                    height: '100%', 
                    bgcolor: 'divider' 
                  }} 
                />
              </Grid>
            </>
          )}

          {/* Feedback form */}
          <Grid item xs={12} md={isMobile ? 12 : 6}>
          
            <Typography 
              variant="h5" 
              style={{ color: '#063bd0' }} 
              gutterBottom
            >
              <b>{t('feedback')}</b>
            </Typography>
            
            <form onSubmit={handleSubmit} noValidate className="feedbackForm">
            <TextField
                select
                label={t("selectContact")}
                name='area'
                fullWidth
                value={formData.area}
                onChange={handleChange}
                
              >
                <MenuItem value="physio">{t('contact_physio')}</MenuItem>
                <MenuItem value="technical">{t('contact_technical')}</MenuItem>
                <MenuItem value="general">{t('contact_general')}</MenuItem>
                {isLoged && <MenuItem value="sales">{t('contact_sales')}</MenuItem>}
              </TextField> 
              <TextField
                label={t("name")}
                name="name"
                fullWidth
                margin="normal"
                required
                value={formData.name}
                onChange={handleChange}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || ''}
              />

              <TextField
                label={"Email"}
                name="email"
                fullWidth
                margin="normal"
                required
                value={formData.email}
                onChange={handleChange}
                error={!!fieldErrors.email || (formData.email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))}
                helperText={fieldErrors.email || (formData.email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? t('invalidEmail') : '')}
              />

              <TextField
                label={t('feedback')}
                name="message"
                fullWidth
                margin="normal"
                multiline
                rows={5}
                required
                value={formData.message}
                onChange={handleChange}
                error={!!fieldErrors.message}
                helperText={fieldErrors.message || ''}
              />
              
              <Box sx={{ position: 'relative', marginTop: 2 }}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  fullWidth
                  
                  sx={{ marginTop: 2 }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {t('sendingFeedback')}
                    </>
                  ) : (
                    t('sendFeedback')
                  )}
                </Button>
              </Box>
            </form>
          </Grid>
        </Grid>
      </div>

      {/* Success Dialog */}
      <Dialog 
        open={isSent} 
        onClose={()=>setIsSent(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('feedbackThanks')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('feedbackAppreciation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={()=>setIsSent(false)} 
            color="primary"
            variant="contained"
          >
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
      

      {/* Error Snackbar */}
      <Snackbar
        open={sendFailed}
        autoHideDuration={6000}
        onClose={() => setSendFailed(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSendFailed(false)}
          severity="error"
          variant="filled"
        >
          {t('issueSendingFeedback')}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FeedbackComponent; 